import i18nFactory from '@yandex-int/i18n';
import { Spin } from 'lego-on-react';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { RecommendationCharacter, RecommendationType } from '../../typings/recommendations';
import { HoverTooltipWrapper } from '../HoverTooltipWrapper/HoverTooltipWrapper';
import { RecommendationIcon } from '../RecommendationIcon/RecommendationIcon';
import { Shape2, Shape2Icon } from '../Shape2/Shape2';

import './RecommendationCard.css';
import * as keyset from './RecommendationCard.i18n';

const i18n = i18nFactory(keyset);

export enum RecommendationCardView {
    normal = 'normal',
    short = 'short',
    recommendationItemsScreen = 'recommendation-items-page',
    recommendationsPopup = 'recommendations-popup',
    recommendationsModal = 'recommendations-modal'
}

export interface RecommendationCardProps {
    icon: Shape2Icon;
    character: RecommendationCharacter;
    title?: string;
    titleHint?: string;
    descriptionInfo?: React.ReactNode;
    buttons?: Array<React.ReactNode>;
    view: RecommendationCardView;
    type: RecommendationType;
    isApplicable?: boolean;
    isInProgress: boolean;
    progressHint?: string;
    onShow?: (view: RecommendationCardView, type: RecommendationType) => void;
}

const BLOCK = 'recommendation-card';

export class RecommendationCard extends React.PureComponent<RecommendationCardProps> {
    render() {
        const { title, children, icon, character, view } = this.props;

        return (
            <div
                className={Bem.cls({
                    block: BLOCK,
                    mods: {
                        view: view
                    }
                })}
            >
                <div className={Bem.cls(BLOCK, 'icon-wrapper')}>
                    <RecommendationIcon cls={Bem.cls(BLOCK, 'icon')} theme={character} icon={icon} title={title} />
                </div>
                <div className={Bem.cls(BLOCK, 'header')}>
                    {this.getTitle()}

                    {this.getTitleHint()}
                </div>

                {this.getDescriptionInfo()}

                <div className={Bem.cls(BLOCK, 'body')}>{children}</div>

                {this.getButtons()}

                {this.getProgressLayer()}
            </div>
        );
    }

    componentDidMount() {
        const { view, type, onShow } = this.props;

        if (onShow) {
            onShow(view, type);
        }
    }

    private getDescriptionInfo() {
        const { descriptionInfo } = this.props;

        if (!descriptionInfo) {
            return null;
        }

        return <div className={Bem.cls(BLOCK, 'description-info')}>{descriptionInfo}</div>;
    }

    private getProgressLayer() {
        const { isInProgress, progressHint } = this.props;

        if (isInProgress === false) {
            return null;
        }

        return (
            <div className={Bem.cls(BLOCK, 'progress-layer')}>
                <Spin progress={true} size="m" />

                <h3 className={Bem.cls(BLOCK, 'progress-layer-title')}>{i18n('progress-title')}</h3>

                {progressHint ? <p className={Bem.cls(BLOCK, 'progress-layer-hint')}>{progressHint}</p> : null}
            </div>
        );
    }

    private getButtons() {
        const { buttons } = this.props;

        if (!buttons || buttons.length === 0) {
            return null;
        }

        return (
            <div className={Bem.cls(BLOCK, 'footer')}>
                {buttons.map((button, index) => (
                    <span key={index} className={Bem.cls(BLOCK, 'footer-btn')}>
                        {button}
                    </span>
                ))}

                {this.getFooterHint()}
            </div>
        );
    }

    private getTitle() {
        const { title } = this.props;

        if (!title) {
            return null;
        }

        return <h3 className={Bem.cls(BLOCK, 'title')}>{title}</h3>;
    }

    private getTitleHint() {
        const { titleHint } = this.props;

        if (!titleHint) {
            return null;
        }

        return <div className={Bem.cls(BLOCK, 'hint')}>{titleHint}</div>;
    }

    private getFooterHint() {
        if (this.props.isApplicable === false) {
            return (
                <HoverTooltipWrapper
                    tooltipText={i18n('no-rights')}
                    to={['top', 'left']}
                    isDoccenterTooltip={false}
                    cls={Bem.cls(BLOCK, 'footer-hint')}
                >
                    <Shape2 width={16} height={16} icon={Shape2Icon.warning} />
                </HoverTooltipWrapper>
            );
        }

        return null;
    }
}
